import { ICON_NAMES, getIcon } from '@utazzo/ui';
import { X } from 'lucide-react';
import React, { useState } from 'react';

interface Props {
  label: string;
  value: unknown;
  onChange: (v: string) => void;
  disabled?: boolean;
}

/**
 * Searchable icon picker backed by the shared @utazzo/ui ICON_REGISTRY (the
 * same set the website renders). Stores the kebab-case icon name.
 */
export const IconPicker = ({ label, value, onChange, disabled }: Props): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const current = typeof value === 'string' ? value : '';
  const Current = getIcon(current);
  const matches = ICON_NAMES.filter((n) => n.includes(q.toLowerCase().trim()));

  const choose = (name: string): void => {
    onChange(name);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative">
      <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center gap-2 rounded-md border border-(--ds-border) bg-(--bg) px-2 text-left text-sm disabled:opacity-50"
      >
        {Current ? (
          <Current className="h-4 w-4 text-(--fg-muted)" />
        ) : (
          <span className="h-4 w-4 rounded bg-(--z-100)" />
        )}
        <span className="flex-1 truncate text-(--fg)">{current || 'No icon'}</span>
      </button>

      {open && !disabled && (
        <>
          {/* click-away */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute z-20 mt-1 w-72 rounded-lg border border-(--ds-border) bg-(--bg) p-2 shadow-lg">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search icons…"
              className="mb-2 h-8 w-full rounded-md border border-(--ds-border) bg-(--bg) px-2 text-sm text-(--fg)"
            />
            <div className="grid max-h-56 grid-cols-6 gap-1 overflow-y-auto">
              <button
                type="button"
                title="No icon"
                onClick={() => choose('')}
                className="flex h-9 items-center justify-center rounded text-(--fg-faint) hover:bg-(--z-50)"
              >
                <X className="h-4 w-4" />
              </button>
              {matches.map((name) => {
                const Icon = getIcon(name)!;
                return (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => choose(name)}
                    className={`flex h-9 items-center justify-center rounded hover:bg-(--z-50) ${
                      current === name ? 'bg-(--accent) text-(--primary)' : 'text-(--fg-muted)'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
              {matches.length === 0 && (
                <p className="col-span-6 py-4 text-center text-xs text-(--fg-faint)">
                  No icons match “{q}”.
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
